/* eslint-disable camelcase */
import { useStoreActions, useStoreState } from 'easy-peasy'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { PRODUCT_PER_PAGE } from 'utils/constants'
import { hasSuperCard } from 'utils/user'

export default function Container({ children }) {
  const router = useRouter()
  const { query } = router
  const { category_slug } = query
  const [products, setProducts] = useState([])
  const [featuredByCategory, setFeaturedByCategory] = useState([])
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(null)
  const [category, setCategory] = useState(null)
  const [recentOrders, setRecentOrders] = useState([])

  const state = useStoreState((s) => ({
    products: s.products,
    categories: s.categories,
    me: s.me,
    recentOrdersState: s.recentOrdersState,
    promos: s.promos,
    featuredProducts: s.featuredProducts
  }))

  const actions = useStoreActions((a) => ({
    getProducts: a.getProducts,
    setProducts: a.setProducts,
    setGetRecentOrdersState: a.setGetRecentOrdersState,
    getRecentOrders: a.getRecentOrders,
    getPromos: a.getPromos,
    setFeaturedProducts: a.setFeaturedProducts,
    getFeaturedProducts: a.getFeaturedProducts
  }))

  useEffect(() => {
    if (!category_slug) {
      // only triggers on all menu
      if (!hasSuperCard()) {
        // either use is sc exclusive params or not
        query.is_supercard_exclusive = false
        actions.getFeaturedProducts({ is_supercard_exclusive: false })
        actions.getProducts({
          per_page: PRODUCT_PER_PAGE,
          page: page,
          is_supercard_exclusive: false
        })
      } else {
        actions.getFeaturedProducts()
        actions.getProducts({ per_page: PRODUCT_PER_PAGE, page: page })
      }
    }

    actions.getPromos()

    return () => {
      actions.setProducts({ status: 0, payload: null })
      actions.setFeaturedProducts({ status: 0, payload: null })
      actions.setGetRecentOrdersState({ status: 0, payload: null })
      setPage(1)
      setPageLimit(null)
      setFeaturedByCategory([])
      setProducts([])
    }
  }, [category_slug])

  useEffect(() => {
    if (state.me.status === 2 && state.me.payload.details !== null) {
      actions.getRecentOrders()
    }
  }, [state.me])

  useEffect(() => {
    setProducts([])
    setPage(1)
    setPageLimit(null)
    setFeaturedByCategory([])
    loadCategory()
  }, [category_slug, state.categories, query])

  useEffect(() => {
    const { status, payload } = state.products
    if (status === 2) {
      setPageLimit(payload.total_pages)
      if (page === 1) {
        setProducts(payload.results)
      } else {
        setProducts((prev) =>
          prev ? [...prev, ...payload.results] : [...payload.results]
        )
      }
    }
  }, [state.products])

  useEffect(() => {
    const { status, payload } = state.featuredProducts
    if (status === 2) {
      setFeaturedByCategory(payload.results)
    }
  }, [state.featuredProducts])

  useEffect(() => {
    const { status, payload } = state.recentOrdersState
    if (status === 2) {
      const allRecentOrders = []
      payload.results.forEach((order) => {
        allRecentOrders.push(...order.order_item)
      })

      setRecentOrders(allRecentOrders)
    }
  }, [state.recentOrdersState])

  const loadCategory = () => {
    const { status, payload } = state.categories
    if (
      category_slug &&
      status === 2 &&
      typeof payload.results !== 'undefined'
    ) {
      const cats = payload.results.filter(
        (category) => category.slug === category_slug
      )
      setProducts([])
      if (cats.length > 0) {
        setCategory(cats[0])
        const productListParams = {
          category_slug: category_slug,
          per_page: PRODUCT_PER_PAGE,
          page: 1,
          is_supercard_exclusive: false
        }

        const featureProductParams = {
          category_slug: category_slug
        }

        if (cats[0].is_supercard_exclusive) {
          productListParams.is_supercard_exclusive = true
          featureProductParams.is_supercard_exclusive = true
        } else {
          if (hasSuperCard()) {
            delete productListParams.is_supercard_exclusive
            delete featureProductParams.is_supercard_exclusive
          }
        }

        actions.getProducts(productListParams)
        actions.getFeaturedProducts(featureProductParams)
      }
    }
  }

  useEffect(() => {
    if (page > 1) {
      actions.getProducts({ per_page: PRODUCT_PER_PAGE, page: page, ...query })
    }
  }, [page])

  const loadMore = () => {
    setPage(page + 1)
  }

  return children({
    state,
    category,
    products,
    featuredByCategory,
    loadMore,
    PRODUCT_PER_PAGE,
    recentOrders,
    pageLimit,
    page
  })
}
